// Per-industry configs for the generic demo templates.
// Keep names simple and generic ("Demo · Barbershop"). No fictional brands,
// no client names. The demo shows a working widget pattern — the owner
// replaces logo + service list + colors when they sign up.

import type { Lang } from './shared/i18n-ctx'
type L = Record<Lang, string>

// ─────────────────── Booking demos (13 industries) ───────────────────

export type BookingService = { name: string; duration: string; price: number }
export type BookingConfig = {
  slug: string
  industry: L
  tagline: L
  icon: string
  services: BookingService[]
}

export const BOOKING_CONFIGS: Record<string, BookingConfig> = {
  barbershop: {
    slug: 'barbershop',
    industry: { en: 'Barbershop', mk: 'Берберница', gr: 'Κουρείο' },
    tagline: {
      en: 'Customers book online. Barbers see today\'s schedule. Fewer walkouts.',
      mk: 'Клиентите резервираат онлајн. Берберот гледа распоред. Помалку изгубени клиенти.',
      gr: 'Πελάτες κλείνουν online. Οι κουρείς βλέπουν το πρόγραμμα της ημέρας.',
    },
    icon: '💈',
    services: [
      { name: 'Classic haircut', duration: '30 min', price: 10 },
      { name: 'Beard trim', duration: '15 min', price: 6 },
      { name: 'Haircut + beard', duration: '45 min', price: 14 },
      { name: 'Kid\'s haircut', duration: '20 min', price: 7 },
      { name: 'Hot shave', duration: '30 min', price: 12 },
    ],
  },
  'hair-salon': {
    slug: 'hair-salon',
    industry: { en: 'Hair Salon', mk: 'Фризерски салон', gr: 'Κομμωτήριο' },
    tagline: {
      en: 'Service-aware time blocks. Deposits for high-value colors. Stylist matching.',
      mk: 'Паметни термини по услуга. Аванси за бојадисување. Усогласен фризер.',
      gr: 'Έξυπνα ραντεβού. Προκαταβολές για βαφή. Αντιστοίχιση κομμώτριας.',
    },
    icon: '💇',
    services: [
      { name: 'Women\'s cut', duration: '45 min', price: 25 },
      { name: 'Color + cut', duration: '2h 30min', price: 75 },
      { name: 'Highlights', duration: '3h', price: 95 },
      { name: 'Blowout', duration: '30 min', price: 18 },
      { name: 'Keratin treatment', duration: '2h', price: 85 },
    ],
  },
  'nail-salon': {
    slug: 'nail-salon',
    industry: { en: 'Nail Salon', mk: 'Салон за нокти', gr: 'Nail Salon' },
    tagline: {
      en: 'Auto-rebook SMS every 3 weeks. Color library. Loyalty program.',
      mk: 'СМС потсетник за нова посета на 3 недели. Библиотека на бои. Лојалност.',
      gr: 'Αυτόματο SMS υπενθύμισης κάθε 3 εβδομάδες.',
    },
    icon: '💅',
    services: [
      { name: 'Classic manicure', duration: '30 min', price: 12 },
      { name: 'Gel polish', duration: '45 min', price: 20 },
      { name: 'Full acrylic set', duration: '90 min', price: 35 },
      { name: 'Pedicure', duration: '45 min', price: 20 },
      { name: 'Nail art', duration: '+ 15 min/finger', price: 5 },
    ],
  },
  'beauty-spa': {
    slug: 'beauty-spa',
    industry: { en: 'Beauty Spa', mk: 'Козметички салон', gr: 'Spa ομορφιάς' },
    tagline: {
      en: 'Packages, memberships, gift cards — all online.',
      mk: 'Пакети, членства, подароци — сѐ онлајн.',
      gr: 'Πακέτα, συνδρομές, δωροκάρτες online.',
    },
    icon: '🧖',
    services: [
      { name: 'Classic facial', duration: '60 min', price: 45 },
      { name: 'Deep-cleansing facial', duration: '90 min', price: 65 },
      { name: 'Back massage', duration: '30 min', price: 25 },
      { name: 'Full-body massage', duration: '60 min', price: 45 },
      { name: 'Hot stone therapy', duration: '75 min', price: 60 },
    ],
  },
  'tattoo-studio': {
    slug: 'tattoo-studio',
    industry: { en: 'Tattoo Studio', mk: 'Тетоважа студио', gr: 'Tattoo Studio' },
    tagline: {
      en: 'Deposit required. Consultation vs session slots. Reference image upload.',
      mk: 'Потребен аванс. Консултации и сесии посебно. Прикачување референтна слика.',
      gr: 'Προκαταβολή υποχρεωτική. Consultation & session ξεχωριστά.',
    },
    icon: '🎨',
    services: [
      { name: 'Consultation (free)', duration: '30 min', price: 0 },
      { name: 'Small tattoo (≤ 1h)', duration: '1h', price: 80 },
      { name: 'Medium tattoo', duration: '3h', price: 220 },
      { name: 'Large / sleeve session', duration: '5h', price: 400 },
      { name: 'Cover-up consultation', duration: '45 min', price: 15 },
    ],
  },
  dentist: {
    slug: 'dentist',
    industry: { en: 'Dentist Clinic', mk: 'Стоматолошка ординација', gr: 'Οδοντίατρος' },
    tagline: {
      en: 'Reminder SMS cut no-shows by 40%. 6-month recall automation.',
      mk: 'СМС намалуваат изостанок за 40%. Автоматски потсетник секои 6 месеци.',
      gr: 'SMS υπενθύμισης για λιγότερες απουσίες.',
    },
    icon: '🦷',
    services: [
      { name: 'Check-up + cleaning', duration: '45 min', price: 40 },
      { name: 'Tooth filling', duration: '30 min', price: 30 },
      { name: 'Teeth whitening', duration: '60 min', price: 150 },
      { name: 'Root canal', duration: '90 min', price: 180 },
      { name: 'Emergency consultation', duration: '20 min', price: 25 },
    ],
  },
  'medical-clinic': {
    slug: 'medical-clinic',
    industry: { en: 'Medical Clinic', mk: 'Медицинска ординација', gr: 'Ιατρείο' },
    tagline: {
      en: 'Triage by symptom. Route to the right doctor. Cut reception calls in half.',
      mk: 'Тријажа по симптом. Распоред кај соодветен лекар.',
      gr: 'Κατανομή ανά σύμπτωμα. Μείωση τηλεφωνικών κλήσεων.',
    },
    icon: '🏥',
    services: [
      { name: 'General check-up', duration: '30 min', price: 25 },
      { name: 'Blood test', duration: '15 min', price: 20 },
      { name: 'Specialist consult', duration: '45 min', price: 40 },
      { name: 'Pediatric visit', duration: '30 min', price: 30 },
      { name: 'Prescription refill', duration: '15 min', price: 10 },
    ],
  },
  physio: {
    slug: 'physio',
    industry: { en: 'Physiotherapist', mk: 'Физиотерапевт', gr: 'Φυσιοθεραπευτής' },
    tagline: {
      en: 'Sell 10-session packages online. Track progress per client.',
      mk: 'Продавај пакети од 10 сесии. Следи напредок.',
      gr: 'Πακέτα 10 συνεδριών online. Παρακολούθηση προόδου.',
    },
    icon: '🧘',
    services: [
      { name: 'Initial assessment', duration: '45 min', price: 30 },
      { name: 'Single session', duration: '45 min', price: 25 },
      { name: 'Package of 5', duration: '—', price: 115 },
      { name: 'Package of 10', duration: '—', price: 220 },
      { name: 'Home visit', duration: '60 min', price: 40 },
    ],
  },
  'yoga-studio': {
    slug: 'yoga-studio',
    industry: { en: 'Yoga Studio', mk: 'Јога студио', gr: 'Yoga Studio' },
    tagline: {
      en: 'Class schedule. 10-packs. Waitlist auto-fill.',
      mk: 'Распоред на часови. Пакети од 10. Автоматска чекална.',
      gr: 'Πρόγραμμα μαθημάτων. Πακέτα. Λίστα αναμονής.',
    },
    icon: '🧘‍♀️',
    services: [
      { name: 'Drop-in class', duration: '60 min', price: 8 },
      { name: '10-class pack', duration: '—', price: 70 },
      { name: 'Unlimited month', duration: '—', price: 60 },
      { name: 'Private session', duration: '60 min', price: 30 },
      { name: 'Yoga + sound-bath', duration: '90 min', price: 15 },
    ],
  },
  gym: {
    slug: 'gym',
    industry: { en: 'Gym', mk: 'Фитнес клуб', gr: 'Γυμναστήριο' },
    tagline: {
      en: 'Auto-renewing memberships. QR entry. Win-back SMS for lapsed members.',
      mk: 'Автоматско обновување. Влез со QR. СМС за неактивни членови.',
      gr: 'Αυτόματες ανανεώσεις. QR είσοδος. SMS επιστροφής.',
    },
    icon: '🏋️',
    services: [
      { name: '1-day pass', duration: '—', price: 5 },
      { name: 'Monthly membership', duration: '—', price: 30 },
      { name: 'Quarterly', duration: '—', price: 80 },
      { name: 'Annual', duration: '—', price: 280 },
      { name: 'Personal trainer (1h)', duration: '60 min', price: 20 },
    ],
  },
  'driving-school': {
    slug: 'driving-school',
    industry: { en: 'Driving School', mk: 'Авто-школа', gr: 'Σχολή οδηγών' },
    tagline: {
      en: 'Book instructor hours. Student progress. Package sales.',
      mk: 'Резервирај часови. Следи напредок. Продавај пакети.',
      gr: 'Κλείσε ώρες. Πρόοδος μαθητή. Πακέτα.',
    },
    icon: '🚙',
    services: [
      { name: '1-hour driving lesson', duration: '60 min', price: 18 },
      { name: 'Theory class', duration: '90 min', price: 12 },
      { name: 'Package (10h)', duration: '—', price: 160 },
      { name: 'Package (20h)', duration: '—', price: 300 },
      { name: 'Exam preparation', duration: '90 min', price: 25 },
    ],
  },
  photography: {
    slug: 'photography',
    industry: { en: 'Photography Studio', mk: 'Фото студио', gr: 'Φωτογραφικό στούντιο' },
    tagline: {
      en: 'Online booking. Deposits. Private gallery delivery.',
      mk: 'Онлајн резервација. Аванс. Приватна галерија.',
      gr: 'Online κράτηση. Προκαταβολή. Galerie πελατών.',
    },
    icon: '📸',
    services: [
      { name: 'Portrait session', duration: '1h', price: 80 },
      { name: 'Family session', duration: '2h', price: 150 },
      { name: 'Wedding (half-day)', duration: '4h', price: 450 },
      { name: 'Wedding (full-day)', duration: '8h', price: 800 },
      { name: 'Product / food photo', duration: '2h', price: 180 },
    ],
  },
  veterinarian: {
    slug: 'veterinarian',
    industry: { en: 'Veterinary Clinic', mk: 'Ветеринарна ординација', gr: 'Κτηνιατρείο' },
    tagline: {
      en: 'Pet profiles. Vaccine reminders. Emergency slot reservation.',
      mk: 'Профил на миленикот. Потсетници за вакцинации.',
      gr: 'Προφίλ κατοικιδίου. Υπενθυμίσεις εμβολίων.',
    },
    icon: '🐾',
    services: [
      { name: 'Check-up', duration: '20 min', price: 20 },
      { name: 'Vaccination', duration: '15 min', price: 25 },
      { name: 'Neutering / spaying', duration: '—', price: 120 },
      { name: 'Dental cleaning', duration: '—', price: 80 },
      { name: 'Emergency consult', duration: '30 min', price: 45 },
    ],
  },
  'event-venue': {
    slug: 'event-venue',
    industry: { en: 'Event Venue', mk: 'Сала за настани', gr: 'Χώρος εκδηλώσεων' },
    tagline: {
      en: 'Halls, weddings, conferences — booked online with a deposit, no email back-and-forth.',
      mk: 'Сали, свадби, конференции — резервирани онлајн со аванс.',
      gr: 'Αίθουσες, γάμοι, συνέδρια — κράτηση online με προκαταβολή.',
    },
    icon: '🎉',
    services: [
      { name: 'Venue viewing (free)', duration: '45 min', price: 0 },
      { name: 'Conference hall (half-day)', duration: '4h', price: 300 },
      { name: 'Conference hall (full-day)', duration: '8h', price: 500 },
      { name: 'Wedding package', duration: 'Full day', price: 1800 },
      { name: 'Birthday / private party', duration: '5h', price: 450 },
    ],
  },
  'equipment-rental': {
    slug: 'equipment-rental',
    industry: { en: 'Equipment Rental', mk: 'Изнајмување опрема', gr: 'Ενοικίαση εξοπλισμού' },
    tagline: {
      en: 'Pick the gear, pick the dates, pay the deposit — availability is always live.',
      mk: 'Избери опрема, избери датуми, плати аванс — достапноста е во живо.',
      gr: 'Διάλεξε εξοπλισμό και ημερομηνίες — διαθεσιμότητα σε πραγματικό χρόνο.',
    },
    icon: '🛠️',
    services: [
      { name: 'Power drill (per day)', duration: '1 day', price: 8 },
      { name: 'Mini excavator (per day)', duration: '1 day', price: 120 },
      { name: 'Scaffold set (per week)', duration: '7 days', price: 90 },
      { name: 'Party tent + chairs (weekend)', duration: '2 days', price: 150 },
      { name: 'PA / sound system (per day)', duration: '1 day', price: 60 },
    ],
  },
  lawyer: {
    slug: 'lawyer',
    industry: { en: 'Law Firm', mk: 'Адвокатска канцеларија', gr: 'Δικηγορικό γραφείο' },
    tagline: {
      en: 'Clients book a consultation by case type. Intake form captures the details first.',
      mk: 'Клиентите закажуваат консултација по тип на случај.',
      gr: 'Οι πελάτες κλείνουν ραντεβού ανά τύπο υπόθεσης.',
    },
    icon: '⚖️',
    services: [
      { name: 'Initial consultation', duration: '30 min', price: 40 },
      { name: 'Contract review', duration: '60 min', price: 90 },
      { name: 'Company formation', duration: '—', price: 250 },
      { name: 'Real-estate closing', duration: '90 min', price: 180 },
      { name: 'Family law consult', duration: '60 min', price: 80 },
    ],
  },
  accountant: {
    slug: 'accountant',
    industry: { en: 'Accountant', mk: 'Сметководител', gr: 'Λογιστής' },
    tagline: {
      en: 'Book by service. Recurring monthly bookkeeping sold as a package.',
      mk: 'Закажи по услуга. Месечно книговодство како пакет.',
      gr: 'Κράτηση ανά υπηρεσία. Μηνιαία λογιστική ως πακέτο.',
    },
    icon: '🧮',
    services: [
      { name: 'Free intro call', duration: '20 min', price: 0 },
      { name: 'Annual tax return', duration: '—', price: 120 },
      { name: 'Monthly bookkeeping', duration: '—', price: 80 },
      { name: 'Payroll (per employee)', duration: '—', price: 12 },
      { name: 'VAT filing', duration: '—', price: 45 },
    ],
  },
  notary: {
    slug: 'notary',
    industry: { en: 'Notary Office', mk: 'Нотарска канцеларија', gr: 'Συμβολαιογράφος' },
    tagline: {
      en: 'Book the right act, upload documents ahead, skip the waiting room.',
      mk: 'Закажи го точниот акт, прикачи документи однапред.',
      gr: 'Κλείσε τη σωστή πράξη, ανέβασε έγγραφα από πριν.',
    },
    icon: '📜',
    services: [
      { name: 'Document certification', duration: '20 min', price: 15 },
      { name: 'Power of attorney', duration: '30 min', price: 30 },
      { name: 'Property sale contract', duration: '60 min', price: 150 },
      { name: 'Inheritance declaration', duration: '45 min', price: 90 },
      { name: 'Company statute', duration: '45 min', price: 70 },
    ],
  },
}

// ─────────────────── Order / menu demos (5 industries) ───────────────────

export type OrderItem = { name: string; description?: string; price: number; tag?: string }
export type OrderConfig = {
  slug: string
  industry: L
  tagline: L
  icon: string
  mode: 'delivery' | 'pickup' | 'table'
  items: OrderItem[]
}

export const ORDER_CONFIGS: Record<string, OrderConfig> = {
  'coffee-bar': {
    slug: 'coffee-bar',
    industry: { en: 'Coffee Bar', mk: 'Кафе бар', gr: 'Καφετέρια' },
    tagline: {
      en: 'Mobile order ahead. Skip the morning queue. Loyalty built in.',
      mk: 'Нарачај преку телефон. Прескокни ја утринската редица.',
      gr: 'Παραγγελία από κινητό. Καμία ουρά.',
    },
    icon: '☕',
    mode: 'pickup',
    items: [
      { name: 'Espresso', price: 1.2 },
      { name: 'Cappuccino', price: 1.8 },
      { name: 'Flat white', price: 2.2 },
      { name: 'Latte', description: 'Hot or iced', price: 2.4 },
      { name: 'Croissant', price: 1.5, tag: 'Bakery' },
      { name: 'Avocado toast', price: 4.5, tag: 'Kitchen' },
      { name: 'Smoothie bowl', price: 5.5, tag: 'Kitchen' },
    ],
  },
  pizzeria: {
    slug: 'pizzeria',
    industry: { en: 'Pizzeria', mk: 'Пицерија', gr: 'Πιτσαρία' },
    tagline: {
      en: 'Your own delivery site. Zero Glovo / Wolt commission.',
      mk: 'Твоја сајт за достава. Без провизија за Glovo / Wolt.',
      gr: 'Δικό σου site delivery. Χωρίς προμήθειες.',
    },
    icon: '🍕',
    mode: 'delivery',
    items: [
      { name: 'Margherita', description: 'Tomato, mozzarella, basil', price: 6.5 },
      { name: 'Pepperoni', description: 'Tomato, mozzarella, pepperoni', price: 7.5 },
      { name: 'Four cheese', price: 8.5 },
      { name: 'Prosciutto e funghi', price: 8.5 },
      { name: 'BBQ chicken', price: 9.5 },
      { name: 'Vegetarian', price: 7.5 },
      { name: 'Garlic bread', price: 3.5, tag: 'Side' },
      { name: 'Coke / Sprite / Water', price: 1.5, tag: 'Drink' },
    ],
  },
  bakery: {
    slug: 'bakery',
    industry: { en: 'Bakery', mk: 'Пекарница', gr: 'Αρτοποιείο' },
    tagline: {
      en: 'Pre-orders for pickup. Custom cakes with deposit. Event catering.',
      mk: 'Пред-нарачки. Колачи по нарачка со аванс. Кетеринг.',
      gr: 'Προπαραγγελίες. Τούρτες με προκαταβολή.',
    },
    icon: '🥐',
    mode: 'pickup',
    items: [
      { name: 'Baguette (500g)', price: 1.8 },
      { name: 'Burek (meat)', price: 2.5, tag: 'Savoury' },
      { name: 'Burek (spinach & cheese)', price: 2.2, tag: 'Savoury' },
      { name: 'Baklava (slice)', price: 1.5, tag: 'Sweet' },
      { name: 'Tulumbi (box of 6)', price: 4, tag: 'Sweet' },
      { name: 'Custom birthday cake', description: 'Order 48h ahead, deposit required', price: 35, tag: 'Order-ahead' },
    ],
  },
  'bar-pub': {
    slug: 'bar-pub',
    industry: { en: 'Bar / Pub', mk: 'Бар / паб', gr: 'Bar / Pub' },
    tagline: {
      en: 'Table reservation. Event RSVP. Private party inquiries.',
      mk: 'Резервирај маса. RSVP за настани. Приватни прослави.',
      gr: 'Κράτηση τραπεζιού. Εκδηλώσεις. Ιδιωτικά πάρτι.',
    },
    icon: '🍻',
    mode: 'table',
    items: [
      { name: 'Craft beer (0.5L)', price: 3 },
      { name: 'Rakija (50ml)', price: 2 },
      { name: 'House wine (glass)', price: 2.5 },
      { name: 'Gin & tonic', price: 5 },
      { name: 'Charcuterie board', price: 10, tag: 'Share' },
      { name: 'Nachos', price: 6, tag: 'Share' },
      { name: 'Live-music ticket', price: 8, tag: 'Event' },
    ],
  },
  'ice-cream': {
    slug: 'ice-cream',
    industry: { en: 'Ice Cream Shop', mk: 'Сладоледаџилница', gr: 'Παγωτατζίδικο' },
    tagline: {
      en: 'Catering orders. Flavor-of-the-week SMS. Loyalty card built in.',
      mk: 'Кетеринг нарачки. СМС за новиот вкус. Картичка за лојалност.',
      gr: 'Catering. SMS γεύσεις εβδομάδας.',
    },
    icon: '🍦',
    mode: 'pickup',
    items: [
      { name: 'Single scoop', price: 1.5 },
      { name: 'Double scoop', price: 2.5 },
      { name: 'Milkshake', price: 3.5 },
      { name: 'Sundae (3 scoops + topping)', price: 5 },
      { name: 'Ice-cream cake (per kg)', price: 16, tag: 'Order-ahead' },
      { name: '20-scoop catering tray', description: 'Events, kids parties', price: 28, tag: 'Catering' },
    ],
  },
  'flower-shop': {
    slug: 'flower-shop',
    industry: { en: 'Flower Shop', mk: 'Цвеќарница', gr: 'Ανθοπωλείο' },
    tagline: {
      en: 'Order bouquets for same-day delivery. Subscriptions and event flowers built in.',
      mk: 'Нарачај букети со достава истиот ден. Претплати и цвеќе за настани.',
      gr: 'Παράγγειλε μπουκέτα με αυθημερόν παράδοση.',
    },
    icon: '💐',
    mode: 'delivery',
    items: [
      { name: 'Mixed seasonal bouquet', price: 18 },
      { name: 'Red roses (12)', price: 25 },
      { name: 'Orchid pot', price: 22 },
      { name: 'Condolence wreath', price: 45, tag: 'Event' },
      { name: 'Wedding centerpiece', description: 'Order 72h ahead', price: 60, tag: 'Order-ahead' },
      { name: 'Weekly flower subscription', price: 15, tag: 'Subscription' },
    ],
  },
  'clothing-boutique': {
    slug: 'clothing-boutique',
    industry: { en: 'Clothing Boutique', mk: 'Бутик за облека', gr: 'Boutique ρούχων' },
    tagline: {
      en: 'Browse the collection, reserve your size in-store, or order for pickup.',
      mk: 'Разгледај колекција, резервирај големина, или нарачај за подигање.',
      gr: 'Δες τη συλλογή, κράτησε το νούμερό σου, ή παράγγειλε.',
    },
    icon: '👗',
    mode: 'pickup',
    items: [
      { name: 'Summer dress', price: 39 },
      { name: 'Linen shirt', price: 29 },
      { name: 'Denim jacket', price: 55 },
      { name: 'Leather handbag', price: 79, tag: 'Accessories' },
      { name: 'Reserve fitting (free)', description: 'Hold items to try in-store', price: 0, tag: 'In-store' },
    ],
  },
  pharmacy: {
    slug: 'pharmacy',
    industry: { en: 'Pharmacy', mk: 'Аптека', gr: 'Φαρμακείο' },
    tagline: {
      en: 'Reserve OTC products and refills online, pick up without the queue.',
      mk: 'Резервирај лекови без рецепт онлајн, подигни без редица.',
      gr: 'Κράτησε προϊόντα online, παρέλαβε χωρίς ουρά.',
    },
    icon: '💊',
    mode: 'pickup',
    items: [
      { name: 'Pain relief (paracetamol)', price: 3.5 },
      { name: 'Vitamin C 1000mg', price: 6 },
      { name: 'Cold & flu pack', price: 8.5 },
      { name: 'Baby care bundle', price: 14, tag: 'Family' },
      { name: 'Prescription refill', description: 'Upload prescription, we prepare it', price: 0, tag: 'Rx' },
    ],
  },
  'pet-shop': {
    slug: 'pet-shop',
    industry: { en: 'Pet Shop', mk: 'Продавница за миленици', gr: 'Pet Shop' },
    tagline: {
      en: 'Food, toys, and supplies with home delivery. Auto-reorder for regulars.',
      mk: 'Храна, играчки и опрема со достава. Автоматска нарачка за редовни.',
      gr: 'Τροφές, παιχνίδια, με παράδοση στο σπίτι.',
    },
    icon: '🐶',
    mode: 'delivery',
    items: [
      { name: 'Dry dog food (10kg)', price: 28 },
      { name: 'Cat litter (5L)', price: 7 },
      { name: 'Chew toy bundle', price: 12 },
      { name: 'Aquarium starter kit', price: 35, tag: 'Starter' },
      { name: 'Monthly food subscription', price: 25, tag: 'Subscription' },
    ],
  },
  'jewelry-store': {
    slug: 'jewelry-store',
    industry: { en: 'Jewelry Store', mk: 'Златарница', gr: 'Κοσμηματοπωλείο' },
    tagline: {
      en: 'Showcase the collection, reserve for viewing, request custom pieces with a deposit.',
      mk: 'Прикажи колекција, резервирај за разгледување, нарачај по мерка.',
      gr: 'Συλλογή, κράτηση για παρουσίαση, custom κομμάτια.',
    },
    icon: '💍',
    mode: 'pickup',
    items: [
      { name: 'Silver necklace', price: 45 },
      { name: 'Gold ring (14k)', price: 180 },
      { name: 'Diamond stud earrings', price: 320 },
      { name: 'Watch battery + service', price: 8, tag: 'Service' },
      { name: 'Custom engagement ring', description: 'Consultation + deposit', price: 0, tag: 'Custom' },
    ],
  },
}

// ─────────────────── Trades / quote-request demos (7 industries) ───────────────────

export type TradesConfig = {
  slug: string
  industry: L
  tagline: L
  icon: string
  services: string[]
  responseTime: L
}

export const TRADES_CONFIGS: Record<string, TradesConfig> = {
  'tile-workers': {
    slug: 'tile-workers',
    industry: { en: 'Tile & Construction', mk: 'Плочкари и градеж', gr: 'Πλακάκια & κατασκευές' },
    tagline: {
      en: 'Quote requests with photos. Project portfolio. Quote → invoice pipeline.',
      mk: 'Понуди со фотографии. Портфолио. Понуда → фактура.',
      gr: 'Προσφορές με φωτογραφίες. Portfolio. Προσφορά → τιμολόγιο.',
    },
    icon: '🧱',
    services: ['Bathroom tiling', 'Kitchen tiling', 'Full renovation', 'Facade work', 'Terrace / exterior'],
    responseTime: { en: 'Quote within 24 hours', mk: 'Одговор за 24 часа', gr: 'Προσφορά σε 24 ώρες' },
  },
  plumber: {
    slug: 'plumber',
    industry: { en: 'Plumber', mk: 'Водоинсталатер', gr: 'Υδραυλικός' },
    tagline: {
      en: 'Emergency routing. Deposit required. Warranty record per job.',
      mk: 'Итни интервенции. Потребен аванс. Гаранција по работа.',
      gr: 'Έκτακτες επεμβάσεις. Προκαταβολή.',
    },
    icon: '🔧',
    services: ['Leak repair', 'Boiler install', 'Drain cleaning', 'Full bathroom plumbing', 'Emergency callout'],
    responseTime: { en: 'Emergency response in 2 hours', mk: 'Итен одговор за 2 часа', gr: 'Έκτακτη απάντηση σε 2 ώρες' },
  },
  electrician: {
    slug: 'electrician',
    industry: { en: 'Electrician', mk: 'Електричар', gr: 'Ηλεκτρολόγος' },
    tagline: {
      en: 'Panel upgrade quotes. PDF safety certificates. Digital log.',
      mk: 'Понуди за табла. PDF сертификат за безбедност.',
      gr: 'Πίνακες. Πιστοποιητικά PDF.',
    },
    icon: '⚡',
    services: ['Electrical panel upgrade', 'New circuit install', 'Lighting design', 'Safety inspection', 'EV charger install'],
    responseTime: { en: 'Quote within 48 hours', mk: 'Понуда за 48 часа', gr: 'Προσφορά σε 48 ώρες' },
  },
  'painter-renovation': {
    slug: 'painter-renovation',
    industry: { en: 'Painter / Renovation', mk: 'Молер и реновирање', gr: 'Βαφέας / ανακαίνιση' },
    tagline: {
      en: 'Photo-upload quotes. Before/after portfolio. Milestone invoicing.',
      mk: 'Понуди од слики. Пред / по галерија.',
      gr: 'Προσφορές από φωτογραφίες. Πριν / μετά.',
    },
    icon: '🎨',
    services: ['Interior painting', 'Exterior facade', 'Full apartment renovation', 'Wallpaper install', 'Plaster / drywall'],
    responseTime: { en: 'Quote within 24 hours', mk: 'Понуда за 24 часа', gr: 'Προσφορά σε 24 ώρες' },
  },
  'cleaning-service': {
    slug: 'cleaning-service',
    industry: { en: 'Cleaning Service', mk: 'Чистење', gr: 'Καθαριότητα' },
    tagline: {
      en: 'Recurring bookings. Staff rota. Photo proof of quality.',
      mk: 'Редовни резервации. Распоред. Фото-докази.',
      gr: 'Τακτικές κρατήσεις. Πρόγραμμα.',
    },
    icon: '🧹',
    services: ['One-time deep clean', 'Weekly apartment', 'Bi-weekly apartment', 'End-of-tenancy clean', 'Office cleaning'],
    responseTime: { en: 'Same-day response', mk: 'Истиот ден', gr: 'Την ίδια μέρα' },
  },
  gardener: {
    slug: 'gardener',
    industry: { en: 'Gardener', mk: 'Градинар', gr: 'Κηπουρός' },
    tagline: {
      en: 'Seasonal contracts. Photo portfolio. Auto-invoice on job done.',
      mk: 'Сезонски договори. Фото портфолио.',
      gr: 'Εποχιακά συμβόλαια. Portfolio.',
    },
    icon: '🌿',
    services: ['Lawn mowing (weekly)', 'Hedge trimming', 'Full garden makeover', 'Tree pruning', 'Seasonal cleanup'],
    responseTime: { en: 'Quote within 24 hours', mk: 'Понуда за 24 часа', gr: 'Προσφορά σε 24 ώρες' },
  },
  'moving-company': {
    slug: 'moving-company',
    industry: { en: 'Moving Company', mk: 'Преселби', gr: 'Μετακομίσεις' },
    tagline: {
      en: 'Video-survey quotes. Crew scheduling. Damage claims portal.',
      mk: 'Понуди по видео. Распоред на екипа.',
      gr: 'Προσφορές από βίντεο. Πρόγραμμα ομάδας.',
    },
    icon: '📦',
    services: ['Local apartment move', 'Office relocation', 'Long-distance move', 'Packing service', 'Furniture assembly'],
    responseTime: { en: 'Quote within 24 hours', mk: 'Понуда за 24 часа', gr: 'Προσφορά σε 24 ώρες' },
  },
  'insurance-agent': {
    slug: 'insurance-agent',
    industry: { en: 'Insurance Agent', mk: 'Осигурителен агент', gr: 'Ασφαλιστικός πράκτορας' },
    tagline: {
      en: 'Clients request a quote by policy type. You reply with a tailored offer — no cold calls.',
      mk: 'Клиентите бараат понуда по тип полиса. Одговараш со прилагодена понуда.',
      gr: 'Οι πελάτες ζητούν προσφορά ανά τύπο ασφάλισης.',
    },
    icon: '🛡️',
    services: ['Car insurance', 'Home insurance', 'Life insurance', 'Travel insurance', 'Business liability'],
    responseTime: { en: 'Personalized quote within 24 hours', mk: 'Понуда за 24 часа', gr: 'Προσφορά σε 24 ώρες' },
  },
  'marketing-agency': {
    slug: 'marketing-agency',
    industry: { en: 'Marketing Agency', mk: 'Маркетинг агенција', gr: 'Διαφημιστική εταιρεία' },
    tagline: {
      en: 'Prospects pick a service and describe the goal. You get a qualified brief, not a vague DM.',
      mk: 'Клиентите избираат услуга и опишуваат цел. Добиваш квалификуван бриф.',
      gr: 'Οι πελάτες επιλέγουν υπηρεσία και περιγράφουν τον στόχο.',
    },
    icon: '📈',
    services: ['Social media management', 'Google / Meta ads', 'Website build', 'SEO', 'Brand & logo design'],
    responseTime: { en: 'Proposal within 48 hours', mk: 'Понуда за 48 часа', gr: 'Πρόταση σε 48 ώρες' },
  },
  custom: {
    slug: 'custom',
    industry: { en: 'Custom Build', mk: 'Решение по мерка', gr: 'Custom λύση' },
    tagline: {
      en: 'No template fits? Describe the idea and budget — get a tailored build proposal.',
      mk: 'Нема шаблон? Опиши ја идејата и буџетот — добиј понуда по мерка.',
      gr: 'Δεν ταιριάζει template; Περίγραψε την ιδέα και τον προϋπολογισμό.',
    },
    icon: '✨',
    services: ['Booking system', 'Online ordering', 'Rental / availability', 'Quote & lead capture', 'Something else entirely'],
    responseTime: { en: 'Build proposal within 48 hours', mk: 'Понуда за 48 часа', gr: 'Πρόταση σε 48 ώρες' },
  },
}
